import React from 'react'
import { useTranslation } from 'react-i18next'
import { Container, Row, Col } from 'react-bootstrap'
import PropTypes from 'prop-types'
import Icon from '../../icon'
import styles from './ProfileHeader.module.scss'

// default cover image (can be changed by user/business via Cover Photos)
const DEFAULT_COVER_IMAGE_PATH = '/images/default-cr-cover-photo.png'

/** platform key (from API or socialLinks) -> Icon component name for social logos */
const SOCIAL_PLATFORM_ICON_NAMES = {
  facebook: 'facebook',
  instagram: 'instagram',
  linkedin: 'linkedin',
  youtube: 'youtube',
  tiktok: 'tiktok',
}

/**
 * full-width profile header, same breakpoints as app; not inside container.
 * variant: 'user' | 'business' – controls layout and which props are used.
 * socialLinks: only links with href are shown; use platform key for the corresponding logo (facebook, instagram, linkedin, youtube, tiktok).
 */
export default function ProfileHeader({
  variant,
  coverImageUrl,
  onCoverPhotosClick,
  // user variant
  avatarUrl,
  name,
  subtitle,
  isFollowing,
  onFollowClick,
  followLoading,
  onShareClick,
  socialLinks,
  // business variant
  logoUrl,
  businessName,
  onUploadLogoClick,
  onEditPageClick,
  businessActions,
}) {
  const { t } = useTranslation()

  // default cover image when none set (can be changed later via Cover Photos button). Gradient fallback if image missing.
  const effectiveCoverUrl = coverImageUrl ?? DEFAULT_COVER_IMAGE_PATH
  const coverStyle = {
    backgroundImage: `url(${effectiveCoverUrl}), linear-gradient(135deg, var(--CRLPURPLE) 0%, var(--CRPURPLE) 50%, var(--CRDPURPLE) 100%)`,
  }

  // same default as account settings (avatar-image / edit.jsx)
  const userAvatarUrl = avatarUrl ?? '/images/default-user.png'

  return (
    <div className={styles.headerWrapper} data-variant={variant}>
      {/* cover section: image only, with avatar + social + buttons on it */}
      <header className={styles.header} style={coverStyle}>
        <div className={styles.coverOverlay} aria-hidden />
        {onCoverPhotosClick && (
          <div className={styles.coverButton}>
            <button
              type="button"
              className={styles.coverPhotosButton}
              onClick={onCoverPhotosClick}
            >
              <Icon name="camera" className={styles.coverPhotosIcon} />
              {t('profiles.coverPhotos', 'Cover Photos')}
            </button>
          </div>
        )}

        <div className={styles.content}>
          {variant === 'user' && (
            <>
              {/* avatar, name, subtitle, following + share, all centered on cover, overlayed on cover photo */}
              <div className={styles.userCoverCenter}>
                <img src={userAvatarUrl} alt="" className={styles.avatar} />
                {name != null && (
                  <h1 className={styles.userCoverName}>{name}</h1>
                )}
                {subtitle != null && (
                  <p className={styles.userCoverSubtitle}>{subtitle}</p>
                )}
                {(onFollowClick || onShareClick) && (
                  <div className={styles.userCoverActions}>
                    {onFollowClick && (
                      <button
                        type="button"
                        className={
                          isFollowing
                            ? `${styles.followButton} ${styles.followButtonFollowing}`
                            : `${styles.followButton} ${styles.followButtonNotFollowing}`
                        }
                        onClick={onFollowClick}
                        disabled={followLoading}
                      >
                        {isFollowing
                          ? t('profiles.following', 'Following')
                          : t('profiles.follow', 'Follow')}
                      </button>
                    )}
                    {onShareClick && (
                      <button
                        type="button"
                        className={styles.shareButton}
                        onClick={onShareClick}
                      >
                        <Icon name="share" className={styles.actionBtnIcon} />
                        {t('share.title', 'Share')}
                      </button>
                    )}
                  </div>
                )}
              </div>
              {/* social media icons in bottom right corner of cover */}
              {Array.isArray(socialLinks) &&
                socialLinks.filter(l => l?.href).length > 0 && (
                  <div className={styles.socialIconsCorner}>
                    {socialLinks
                      .filter(l => l?.href)
                      .map((link, i) => {
                        const iconName =
                          SOCIAL_PLATFORM_ICON_NAMES[link.platform] || null
                        return (
                          <a
                            key={i}
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.socialIcon}
                            aria-label={link.label || link.platform}
                          >
                            {iconName ? (
                              <Icon
                                name={iconName}
                                className={styles.socialIconSvg}
                              />
                            ) : (
                              link.icon ?? link.platform?.charAt(0) ?? '?'
                            )}
                          </a>
                        )
                      })}
                  </div>
                )}
            </>
          )}

          {variant === 'business' && (
            <Container fluid="md" className={styles.businessContainer}>
              <Row className={styles.businessRow}>
                <Col xs={12} lg={3} className={styles.logoCol}>
                  <div className={styles.logoCard}>
                    {logoUrl ? (
                      <img src={logoUrl} alt="" className={styles.logoImg} />
                    ) : (
                      <div className={styles.logoPlaceholder} aria-hidden />
                    )}
                    {onUploadLogoClick && (
                      <button
                        type="button"
                        className={`btn btn-sm ${styles.shareButton}`}
                        onClick={onUploadLogoClick}
                      >
                        <Icon name="camera" className={styles.actionBtnIcon} />
                        {t('profiles.uploadLogo', 'Upload Logo')}
                      </button>
                    )}
                  </div>
                </Col>
                <Col xs={12} lg={6} aria-hidden />
                <Col xs={12} lg={3} className={styles.businessActionsCol}>
                  <div className={styles.businessActions}>
                    {onEditPageClick && (
                      <button
                        type="button"
                        className={`btn btn-sm ${styles.shareButton}`}
                        onClick={onEditPageClick}
                      >
                        <Icon name="edit" className={styles.actionBtnIcon} />
                        {t('profiles.editPage', 'Edit Page')}
                      </button>
                    )}
                    {Array.isArray(socialLinks) &&
                      socialLinks.filter(l => l?.href).length > 0 && (
                        <div className={styles.socialIcons}>
                          {socialLinks
                            .filter(l => l?.href)
                            .map((link, i) => {
                              const iconName =
                                SOCIAL_PLATFORM_ICON_NAMES[link.platform] ||
                                null
                              return (
                                <a
                                  key={i}
                                  href={link.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.socialIcon}
                                  aria-label={link.label || link.platform}
                                >
                                  {iconName ? (
                                    <Icon
                                      name={iconName}
                                      className={styles.socialIconSvg}
                                    />
                                  ) : (
                                    link.icon ?? link.platform?.charAt(0) ?? '?'
                                  )}
                                </a>
                              )
                            })}
                        </div>
                      )}
                    {onFollowClick && (
                      // so currently businesses do not have the ability to be followed, backend updates required
                      <button
                        type="button"
                        className={
                          isFollowing
                            ? `${styles.followButton} ${styles.followButtonFollowing}`
                            : `${styles.followButton} ${styles.followButtonNotFollowing}`
                        }
                        onClick={onFollowClick}
                        disabled={followLoading}
                      >
                        {isFollowing
                          ? t('profiles.following', 'Following')
                          : t('profiles.follow', 'Follow')}
                      </button>
                    )}
                    {onShareClick && (
                      <button
                        type="button"
                        className={`btn btn-sm ${styles.shareButton}`}
                        onClick={onShareClick}
                      >
                        <Icon name="share" className={styles.actionBtnIcon} />
                        {t('share.title', 'Share')}
                      </button>
                    )}
                    {businessActions}
                  </div>
                </Col>
              </Row>
            </Container>
          )}
        </div>
      </header>
    </div>
  )
}

ProfileHeader.propTypes = {
  variant: PropTypes.oneOf(['user', 'business']).isRequired,
  coverImageUrl: PropTypes.string,
  onCoverPhotosClick: PropTypes.func,
  avatarUrl: PropTypes.string,
  name: PropTypes.string,
  subtitle: PropTypes.string,
  isFollowing: PropTypes.bool,
  onFollowClick: PropTypes.func,
  followLoading: PropTypes.bool,
  onShareClick: PropTypes.func,
  socialLinks: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string,
      label: PropTypes.string,
      platform: PropTypes.string,
      icon: PropTypes.node,
    }),
  ),
  logoUrl: PropTypes.string,
  businessName: PropTypes.string,
  onUploadLogoClick: PropTypes.func,
  onEditPageClick: PropTypes.func,
  businessActions: PropTypes.node,
}
